/** The most decimal places a rate may have (the server's rule). */
export const MaxRatePlaces = 6;

/** The largest rate the server accepts, in USD per million tokens. */
export const MaxRate = 1000;

/** What a rate field came to: nothing, a valid number, or the reason it cannot be one. */
export type RateParse = { kind: "empty" } | { kind: "ok"; value: number } | { kind: "invalid" } | { kind: "tooManyPlaces" } | { kind: "outOfRange" };

/**
 * Reads the value of a rate's number input. A number input hands back invariant text ("1.68", whatever the person's locale shows), and
 * an empty string for anything it cannot read as a number, so this checks only what the server checks: a plain decimal, at most six
 * places, above zero and at most {@link MaxRate}.
 */
export function parseRate(text: string): RateParse {
  const trimmed = text.trim();
  if (trimmed === "") {
    return { kind: "empty" };
  }

  if (!/^\d+(\.\d+)?$/.test(trimmed) && !/^\.\d+$/.test(trimmed)) {
    return { kind: "invalid" };
  }

  const places = trimmed.includes(".") ? trimmed.length - trimmed.indexOf(".") - 1 : 0;
  if (places > MaxRatePlaces) {
    return { kind: "tooManyPlaces" };
  }

  const value = Number(trimmed);
  return value > 0 && value <= MaxRate ? { kind: "ok", value } : { kind: "outOfRange" };
}

/** A rate as the page shows it: the locale's digits, no trailing zeros, at most six decimal places. */
export function formatRate(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: MaxRatePlaces }).format(value);
}

/** A rate as it is put back into a number input for editing: invariant, with at most six places and no trailing zeros. */
export function rateToInput(value: number): string {
  return String(Number(value.toFixed(MaxRatePlaces)));
}
